import * as React from 'react';

type AddHandler = (anchor: HTMLAnchorElement) => void;

export function useEditorialAnchors(
  containerRef: React.RefObject<HTMLElement | null>,
  addTrackingClickEventCallback: AddHandler,
) {
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const anchors = root.querySelectorAll<HTMLAnchorElement>('a');
    anchors.forEach((a) => {
      addTrackingClickEventCallback(a);
    });

    fixFootnoteAriaLabels(root);

    const observer = new MutationObserver(() => {
      fixFootnoteAriaLabels(root);
    });
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label'],
    });

    return () => {
      observer.disconnect();
    };
  }, [containerRef, addTrackingClickEventCallback]);
}

const fixFootnoteAriaLabels = (root: HTMLElement) => {
  const footnoteLinks = root.querySelectorAll<HTMLAnchorElement>(
    'a[aria-label=""], a:not([aria-label])',
  );

  footnoteLinks.forEach((link) => {
    const isFootnote =
      link.className.includes('footnote') ||
      link.href.includes('#') ||
      link.closest('[class*="footnote"]');

    if (!isFootnote) return;

    const text = link.textContent?.trim() || '';
    let label = 'Footnote reference';

    if (text) label = `Footnote: ${text}`;
    else if (link.href.includes('#')) {
      const id = link.href.split('#')[1];
      if (id) label = `Footnote reference ${id}`;
    }

    link.setAttribute('aria-label', label);
  });
};
